import React, { useEffect, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { InputAdornment } from '@mui/material';

import {
  InputUI,
  AutoCompleteSelectUI,
  FormErrorUI,
  FormLabelUI,
  RadioGroupUI,
  CheckboxUI,
  SelectUI,
} from '../Interface';
import itemService from '../../service/item.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import {
  InvManagementMethodList,
  ItemGroupType,
  ItemGroupTypeList,
  StockArticleTypeList,
} from '../../config/itemGroup';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import {
  buildItemCodeRequest,
  getManualItemCodeSuffix,
  getProtectedManualItemCode,
  isCustomItemCodeGenerationEnabled,
  normalizeGeneratedItemCode,
} from './itemCodeGeneration';

export const ItemFields = ({ viewField, selectedValues, autocompleteOptions, addField, autoCompleteData }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const [itemCodePolicy, setItemCodePolicy] = useState(null);
  const itemCodeGenerationStateRef = useRef({
    isCreateMode: false,
    itemGroupId: undefined,
    autoGenerationEnabled: false,
  });
  const customGenerateRef = useRef({ lastPolicyKey: null, inProgress: false });

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(params).length) getParam();
  }, []);

  const isCreateMode = !viewField && !addField;
  const isCustomItemCodeGeneration = isCreateMode && isCustomItemCodeGenerationEnabled(config);
  const shouldAutoGenerateItemCode = isCustomItemCodeGeneration
    ? values.autoGenerateItemCode ?? itemCodePolicy?.autoGenerateDefault ?? true
    : params.GENERATE_ITEMCODE === 'Y';
  const isAutoGenerateLocked = Boolean(itemCodePolicy?.autoGenerateLocked) || itemCodePolicy?.manualAllowed === false;
  const shouldShowAutoGenerateToggle =
    isCustomItemCodeGeneration && Boolean(itemCodePolicy?.autoGenerateVisible) && !viewField && !addField;
  const manualPrefix =
    isCustomItemCodeGeneration && !shouldAutoGenerateItemCode ? itemCodePolicy?.manualPrefix ?? '' : '';
  const isManualPrefixMode = Boolean(manualPrefix);
  const displayItemCode = isManualPrefixMode
    ? getManualItemCodeSuffix(values.itemCode ?? '', manualPrefix)
    : values.itemCode ?? '';
  const customPolicyRequest = buildItemCodeRequest({ ...values, itemCode: null }, shouldAutoGenerateItemCode);
  const customPolicyRequestKey = JSON.stringify(customPolicyRequest);

  const handlechangeVatCode = (itemType) => {
    const { purchaseVatCodeService, purchaseVatCodeItem, salesVatCodeService, salesVatCodeItem } = config;
    if (itemType === ItemGroupType.SERVICE) {
      setFieldValue('purchaseVatCode', purchaseVatCodeService || null);
      setFieldValue('salesVatCode', salesVatCodeService || null);
    } else if (itemType === ItemGroupType.STOCKARTICLE) {
      setFieldValue('salesVatCode', salesVatCodeItem || null);
      setFieldValue('purchaseVatCode', purchaseVatCodeItem || null);
    }
  };

  const handleGenerateItemCode = async (itemGroupId = values.itemGroupId ?? null) => {
    const response = await itemService.generateItemCode(itemGroupId);
    const generatedItemCode = normalizeGeneratedItemCode(response);

    if (!generatedItemCode) {
      addNotification({
        content: 'A cikkszám generálása sikertelen!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await setFieldValue('itemCode', generatedItemCode);
    validateField('itemCode');
  };

  const handleGenerateItemCodeByPolicy = async () => {
    const response = await itemService.generateItemCodeByStrategy(
      buildItemCodeRequest({ ...values, itemCode: null }, true)
    );
    const generatedItemCode = normalizeGeneratedItemCode(response);

    if (!generatedItemCode) {
      addNotification({
        content: 'A cikkszám generálása sikertelen!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await setFieldValue('itemCode', generatedItemCode, false);
    validateField('itemCode');
  };

  const handleItemCodeChange = (event) => {
    const nextValue = event.target.value ?? '';
    const protectedValue = isManualPrefixMode ? getProtectedManualItemCode(nextValue, manualPrefix) : nextValue;

    setFieldValue('itemCode', protectedValue, false);
  };

  useEffect(() => {
    if (!isCustomItemCodeGeneration) {
      setItemCodePolicy(null);
      return undefined;
    }

    if (!values.itemGroupId) {
      setItemCodePolicy(null);
      return undefined;
    }

    let active = true;

    itemService
      .getItemCodePolicy(customPolicyRequest)
      .then((policy) => {
        if (!active) {
          return;
        }

        setItemCodePolicy(policy ?? null);

        if ((policy?.autoGenerateLocked || policy?.manualAllowed === false) && values.autoGenerateItemCode !== true) {
          setFieldValue('autoGenerateItemCode', true, false);
          return;
        }

        if (values.autoGenerateItemCode === null || values.autoGenerateItemCode === undefined) {
          setFieldValue('autoGenerateItemCode', policy?.autoGenerateDefault ?? true, false);
        }
      })
      .catch(() => {
        if (active) {
          setItemCodePolicy(null);
        }
      });

    return () => {
      active = false;
    };
  }, [
    customPolicyRequestKey,
    isCustomItemCodeGeneration,
    setFieldValue,
    values.autoGenerateItemCode,
    values.itemGroupId,
  ]);

  useEffect(() => {
    if (!isCustomItemCodeGeneration || !isCreateMode || !values.itemGroupId) {
      return;
    }

    if (!shouldAutoGenerateItemCode) {
      customGenerateRef.current.lastPolicyKey = null;
      const protectedValue =
        manualPrefix && !(values.itemCode ?? '').startsWith(manualPrefix) ? manualPrefix : values.itemCode ?? '';
      if (protectedValue !== (values.itemCode ?? '')) {
        setFieldValue('itemCode', protectedValue, false);
      }
      return;
    }

    // Prevent repeated generation loops:
    // - If an itemCode is already set for the same policy request, skip generation.
    // - If a generation is already in progress, skip concurrent calls.
    const run = async () => {
      if (customGenerateRef.current.inProgress) return;
      const policyChanged = customGenerateRef.current.lastPolicyKey !== customPolicyRequestKey;

      // Skip transient renders where an existing item's code is still in Formik during mode switches.
      if (values.itemCode && !policyChanged) {
        return;
      }

      try {
        customGenerateRef.current.inProgress = true;
        await handleGenerateItemCodeByPolicy();
        customGenerateRef.current.lastPolicyKey = customPolicyRequestKey;
      } finally {
        customGenerateRef.current.inProgress = false;
      }
    };

    run();
  }, [
    customPolicyRequestKey,
    isCreateMode,
    isCustomItemCodeGeneration,
    manualPrefix,
    setFieldValue,
    shouldAutoGenerateItemCode,
    values.itemCode,
    values.itemGroupId,
  ]);

  useEffect(() => {
    if (isCustomItemCodeGeneration) {
      return;
    }

    const previousState = itemCodeGenerationStateRef.current;
    const enteredCreateMode = isCreateMode && !previousState.isCreateMode;
    const itemGroupChanged =
      shouldAutoGenerateItemCode &&
      isCreateMode &&
      previousState.isCreateMode &&
      previousState.itemGroupId !== values.itemGroupId;
    const autoGenerationEnabledNow = shouldAutoGenerateItemCode && !previousState.autoGenerationEnabled;

    itemCodeGenerationStateRef.current = {
      isCreateMode,
      itemGroupId: values.itemGroupId,
      autoGenerationEnabled: shouldAutoGenerateItemCode,
    };

    if (!shouldAutoGenerateItemCode || !isCreateMode) {
      return;
    }

    if (enteredCreateMode || itemGroupChanged || autoGenerationEnabledNow) {
      handleGenerateItemCode(values.itemGroupId ?? null);
    }
  }, [isCreateMode, isCustomItemCodeGeneration, shouldAutoGenerateItemCode, values.itemGroupId]);

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="md:col-span-2 col-span-4">
        <InputUI
          fullWidth
          name="itemCode"
          type="text"
          value={displayItemCode}
          onChange={handleItemCodeChange}
          onBlur={() => validateField('itemCode')}
          label={<FormLabelUI text="Cikkszám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          disabled={viewField || addField || (shouldAutoGenerateItemCode && isCreateMode)}
          InputLabelProps={{ shrink: values.itemCode && values.itemCode !== '' }}
          InputProps={
            isManualPrefixMode
              ? {
                  startAdornment: <InputAdornment position="start">{manualPrefix}</InputAdornment>,
                }
              : undefined
          }
        />
      </div>
      <div className="md:col-span-2 col-span-4">
        <Field
          type="text"
          name="itemName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Cikknév" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemName} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.itemName && values.itemName !== '' }}
        />
      </div>
      {shouldShowAutoGenerateToggle && (
        <div className="col-span-4 md:col-span-2 -mt-1">
          <CheckboxUI
            name="autoGenerateItemCode"
            checked={Boolean(shouldAutoGenerateItemCode)}
            onCheckedChange={(checked) => {
              setFieldValue('autoGenerateItemCode', checked, false);

              if (checked) {
                setFieldValue('itemCode', '', false);
              } else {
                const nextItemCode =
                  itemCodePolicy?.manualPrefix && !(values.itemCode ?? '').startsWith(itemCodePolicy.manualPrefix)
                    ? itemCodePolicy.manualPrefix
                    : values.itemCode ?? '';
                setFieldValue('itemCode', nextItemCode, false);
              }
            }}
            label={<FormLabelUI text="Cikkszám generálás" />}
            disabled={viewField || addField || isAutoGenerateLocked}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="itemGroupId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cikkcsoport" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemGroupId} />}
          onChange={(_e, newVal) => {
            setFieldValue('itemGroupId', newVal?.value ?? null).then(() => {
              validateField('itemGroupId');
            });
            setFieldValue('autoGenerateItemCode', null, false);
            // If auto-generation is enabled, clear current itemCode when item group changes
            if (shouldAutoGenerateItemCode) {
              setFieldValue('itemCode', '', false);
            }
            const findedGroup = autoCompleteData.itemGroup.find((i) => i.id === newVal?.value);
            const findedME = autoCompleteData.quantityUnit.find((i) => findedGroup?.quantityUnitId === i?.id);
            setFieldValue('weight', findedME?.weight ?? '');
            setFieldValue('quantityUnitId', findedGroup?.quantityUnitId ?? null);
            setFieldValue('type', findedGroup?.type ?? null);
            setFieldValue('landedCost', false);
            setFieldValue('stockItemType', findedGroup?.stockItemType ?? null);
            setFieldValue('inPurchase', findedGroup?.inPurchase ?? null);
            setFieldValue('inSales', findedGroup?.inSales ?? null);

            if (!values.purchaseVatCode || !values.salesVatCode) {
              handlechangeVatCode(findedGroup?.type ?? null);
            }
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.itemGroupId !== '' }}
          selectedValue={values.itemGroupId}
          selectedLabelValue={selectedValues?.itemGroup?.name}
          dataset={autocompleteOptions?.itemGroup}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="quantityUnitId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Mennyiségi egység" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.quantityUnitId} />}
          onChange={(_e, newVal) => {
            setFieldValue('quantityUnitId', newVal?.value ?? null).then(() => {
              validateField('quantityUnitId');
            });
            const findedME = autoCompleteData.quantityUnit.find((i) => i.id === newVal?.value);
            setFieldValue('weight', findedME?.weight ?? '');
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.quantityUnitId !== '' }}
          selectedValue={values.quantityUnitId}
          selectedLabelValue={selectedValues?.quantityUnit?.name}
          dataset={autocompleteOptions?.quantityUnit}
        />
      </div>
      <div className="col-span-2">
        <Field
          type="radio"
          name="type"
          as={RadioGroupUI}
          row
          label={<FormLabelUI text="Típus" />}
          helperText={<FormErrorUI message={errors.type} />}
          disabled
          value={values.type}
          option={ItemGroupTypeList}
        />
      </div>
      <div className={values.type === ItemGroupType.STOCKARTICLE ? 'col-span-2 pt-3' : 'hidden'}>
        <Field
          name="stockItemType"
          component={SelectUI}
          fullWidth
          option={StockArticleTypeList}
          value={values.stockItemType ?? ''}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue('stockItemType', value ?? null).then(() => {
              validateField('stockItemType');
            });
          }}
          label={<FormLabelUI text="Készletcikk fajta" />}
          helperText={<FormErrorUI message={errors.stockItemType} />}
          disabled
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          name="inPurchase"
          as={CheckboxUI}
          fullWidth
          checked={values.inPurchase ?? false}
          variant="standard"
          onChange={(e) => {
            setFieldValue('inPurchase', e.target.checked ?? null).then(() => {
              validateField('inPurchase');
            });
          }}
          label={<FormLabelUI text="Beszerzésben részt vesz" />}
          helperText={<FormErrorUI message={errors.inPurchase} />}
          disabled={viewField}
        />
        <Field
          name="inSales"
          as={CheckboxUI}
          fullWidth
          checked={values.inSales ?? false}
          variant="standard"
          onChange={(e) => {
            setFieldValue('inSales', e.target.checked ?? null).then(() => {
              validateField('inSales');
            });
          }}
          label={<FormLabelUI text="Értékesítésben részt vesz" />}
          helperText={<FormErrorUI message={errors.inSales} />}
          disabled={viewField}
        />
      </div>
      <div className={values.type === ItemGroupType.STOCKARTICLE ? 'col-span-2 pt-3' : 'hidden'}>
        <Field
          name="invManagementMethod"
          component={SelectUI}
          fullWidth
          option={InvManagementMethodList}
          value={values.invManagementMethod ?? ''}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue('invManagementMethod', value ?? null).then(() => {
              validateField('invManagementMethod');
            });
          }}
          label={<FormLabelUI text="Készletkezelés módja" />}
          helperText={<FormErrorUI message={errors.invManagementMethod} />}
          disabled={viewField}
        />
      </div>
      <div className={values.type === ItemGroupType.STOCKARTICLE ? 'col-span-4 md:col-span-1' : 'hidden'}>
        <Field
          type="number"
          name="weight"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Súly (kg)" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.weight} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.weight && values.weight !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="kshClassificationId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="KSH besorolás" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.kshClassificationId} />}
          onChange={(_e, newVal) => {
            setFieldValue('kshClassificationId', newVal?.value ?? null).then(() => {
              validateField('kshClassificationId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.kshClassificationId !== '' }}
          selectedValue={values.kshClassificationId}
          selectedLabelValue={selectedValues?.kshClassification?.name}
          dataset={autocompleteOptions?.kshClassification}
        />
      </div>
      <div className="md:col-span-2 col-span-4">
        <Field
          type="text"
          name="other"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Egyéb" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.other} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.other && values.other !== '' }}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="text"
          name="barCode"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Vonalkód" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.barCode} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.barCode && values.barCode !== '' }}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="text"
          name="drawingNumber"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Rajzszám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.drawingNumber} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.drawingNumber && values.drawingNumber !== '' }}
        />
      </div>
    </div>
  );
};
